import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Send, Minus } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { services } from "@/data/services";

const serviceOptions = [...services.map((s) => s.title), "Careers", "General Enquiry"];

const empty = { name: "", company: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...empty, service: params.get("service") || "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.service) er.service = "Please select what you need help with.";
    if (form.message.trim().length < 10) er.message = "Please tell us a little more (at least 10 characters).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("sending");
    try {
      await api.post("/enquiries", form);
      setStatus("success");
    } catch (err) {
      setServerError(formatApiError(err));
      setStatus("idle");
    }
  };

  const inputCls = (k) =>
    `w-full rounded-lg border bg-white px-4 py-3 text-sm text-[#172033] outline-none transition-colors placeholder:text-[#98A2B3] focus:border-[#A87B1F] focus:ring-2 focus:ring-[#C9A227]/20 ${
      errors[k] ? "border-red-400" : "border-[#0B1F3A]/15"
    }`;

  return (
    <>
      <Seo
        title="Contact | Incremental Consulting"
        description="Tell us about the programme you need to land. We'll show you how we'd deliver it, resource it, or review whether it's on track."
      />
      <section className="bg-[#0B1F3A]">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C9A227]" data-testid="contact-eyebrow">Contact</p>
            <h1 className="mt-3 max-w-3xl text-balance font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="contact-page-title">
              Tell us about the programme you need to land.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70">
              We'll show you how we'd deliver it, resource it, or review whether it's on track.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F5F8FC]">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <div className="grid gap-14 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <Reveal>
                <h2 className="font-display text-2xl font-bold tracking-tight text-[#0B1F3A]" data-testid="contact-lets-talk">Let's talk.</h2>
                <p className="mt-4 text-[15px] leading-relaxed text-[#172033]/85">
                  A short conversation is the fastest way to see whether we're the right fit for your delivery, PMO, resourcing, training or review need.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h3 className="mt-10 text-xs font-bold uppercase tracking-[0.22em] text-[#A87B1F]">What we can help with</h3>
                <ul className="mt-4 space-y-2.5">
                  {services.map((s) => (
                    <li key={s.slug} className="flex items-center gap-3 text-sm font-medium text-[#172033]/85">
                      <Minus size={14} className="text-[#C9A227]" />
                      {s.title}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.15}>
                <dl className="mt-10 space-y-5" data-testid="contact-details">
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.22em] text-[#A87B1F]">Email</dt>
                    <dd className="mt-1 text-sm"><a href="mailto:takunda.noha@incrementalconsulting.co.za" className="font-medium text-[#172033] hover:text-[#A87B1F]" data-testid="contact-email-link">takunda.noha@incrementalconsulting.co.za</a></dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.22em] text-[#A87B1F]">Phone</dt>
                    <dd className="mt-1 text-sm"><a href="tel:+27608249449" className="font-medium text-[#172033] hover:text-[#A87B1F]" data-testid="contact-phone-link">+27 60 824 9449</a></dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.22em] text-[#A87B1F]">Based in</dt>
                    <dd className="mt-1 text-sm font-medium text-[#172033]">Fourways, Johannesburg, South Africa</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.22em] text-[#A87B1F]">Entity</dt>
                    <dd className="mt-1 text-sm font-medium text-[#172033]">Incremental Consulting (Pty) Ltd<br />Reg. 2023/004592/07</dd>
                  </div>
                </dl>
                <div className="mt-6 flex flex-wrap gap-2" data-testid="contact-badges">
                  {["100% Black-owned", "B-BBEE Level 1", "CSD registered"].map((b) => (
                    <span key={b} className="rounded-full border border-[#A87B1F]/40 px-3 py-1 text-[11px] font-semibold text-[#A87B1F]">{b}</span>
                  ))}
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.1} className="lg:col-span-3">
              <div className="rounded-2xl border border-[#0B1F3A]/10 bg-white p-7 shadow-[0_20px_50px_-30px_rgba(11,31,58,0.3)] sm:p-9">
                {status === "success" ? (
                  <div className="flex flex-col items-start gap-4 py-10" data-testid="contact-success-message">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#C9A227]/15 text-[#A87B1F]">
                      <CheckCircle2 size={28} />
                    </span>
                    <h2 className="font-display text-2xl font-bold text-[#0B1F3A]">Thank you — your message has been sent.</h2>
                    <p className="max-w-md text-sm leading-relaxed text-[#667085]">
                      We have received your message and a confirmation email is on its way to you. We will be in touch shortly.
                    </p>
                    <button
                      onClick={() => { setForm(empty); setStatus("idle"); }}
                      className="mt-2 text-sm font-semibold text-[#A87B1F] hover:text-[#0B1F3A]"
                      data-testid="contact-send-another"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} noValidate data-testid="contact-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="cf-name" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Your name *</label>
                        <input id="cf-name" data-testid="contact-input-name" className={inputCls("name")} value={form.name} onChange={set("name")} placeholder="Full name" />
                        {errors.name && <p className="mt-1 text-xs text-red-500" data-testid="contact-error-name">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="cf-company" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Organisation</label>
                        <input id="cf-company" data-testid="contact-input-company" className={inputCls("company")} value={form.company} onChange={set("company")} placeholder="Company or institution" />
                      </div>
                      <div>
                        <label htmlFor="cf-email" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Work email *</label>
                        <input id="cf-email" type="email" data-testid="contact-input-email" className={inputCls("email")} value={form.email} onChange={set("email")} placeholder="you@company.co.za" />
                        {errors.email && <p className="mt-1 text-xs text-red-500" data-testid="contact-error-email">{errors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="cf-phone" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Phone</label>
                        <input id="cf-phone" data-testid="contact-input-phone" className={inputCls("phone")} value={form.phone} onChange={set("phone")} placeholder="+27 ..." />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="cf-service" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Service of interest *</label>
                        <select id="cf-service" data-testid="contact-select-service" className={inputCls("service")} value={form.service} onChange={set("service")}>
                          <option value="">Select a service…</option>
                          {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {errors.service && <p className="mt-1 text-xs text-red-500" data-testid="contact-error-service">{errors.service}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="cf-message" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">How can we help? *</label>
                        <textarea id="cf-message" rows={5} data-testid="contact-input-message" className={inputCls("message")} value={form.message} onChange={set("message")} placeholder="Tell us about the programme, the deadline, and what's at stake." />
                        {errors.message && <p className="mt-1 text-xs text-red-500" data-testid="contact-error-message">{errors.message}</p>}
                      </div>
                    </div>
                    {serverError && <p className="mt-4 text-sm text-red-500" data-testid="contact-server-error">{serverError}</p>}
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      data-testid="contact-submit-button"
                      className="mt-7 inline-flex items-center gap-2 rounded-md bg-[#C9A227] px-8 py-3.5 text-sm font-bold text-[#0B1F3A] transition-colors duration-200 hover:bg-[#DDB845] disabled:opacity-60"
                    >
                      {status === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      {status === "sending" ? "Sending…" : "Send message"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
